import type { ReactNode } from 'react';
import { renderInline } from './MarkdownText.js';
import { parseDocument, type DocBlock } from './markdownDocumentParse.js';
import styles from './MarkdownDocument.module.css';

/**
 * Block-level markdown renderer for LONG-FORM DOCUMENTS: the versioned legal
 * texts (Terms of Service, Sales Partner Terms) served through TermsVersion.
 *
 * WHY THIS IS NOT MarkdownText
 * ────────────────────────────
 * MarkdownText is the EVIE chat renderer. It wraps everything in a <p>,
 * deliberately, so its block-styled spans stay valid inside a chat bubble. A
 * real <table> or <h3> inside a <p> is invalid HTML; the browser hoists it
 * out of the paragraph and the layout breaks. Rather than reshape a component
 * two shipped chat surfaces depend on, this is a sibling that wraps in a <div>
 * and can therefore emit real block elements.
 *
 * The INLINE pass is imported from MarkdownText, so bold, links, and above all
 * the safeHref URL-scheme guard behave identically in both. The BLOCK pass is
 * imported from markdownDocumentParse, which is pure so it can be tested.
 * This file is only the mapping from blocks to elements.
 */

function renderBlock(b: DocBlock, key: number): ReactNode {
  switch (b.kind) {
    case 'rule':
      return <hr key={key} className={styles.rule} />;
    case 'heading': {
      const Tag = `h${b.depth}` as 'h3';
      return <Tag key={key} className={styles.heading}>{renderInline(b.text)}</Tag>;
    }
    case 'quote':
      return <blockquote key={key} className={styles.quote}>{renderInline(b.text)}</blockquote>;
    case 'list':
      return (
        <ul key={key} className={styles.list}>
          {b.items.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </ul>
      );
    case 'table':
      return (
        // Wrapped so a wide table scrolls INSIDE the document rather than
        // forcing the whole accept card to scroll sideways on a phone.
        <div key={key} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {b.header.map((c, i) => <th key={i} scope="col">{renderInline(c)}</th>)}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{renderInline(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'para':
    default:
      return <p key={key} className={styles.para}>{renderInline(b.text)}</p>;
  }
}

export interface MarkdownDocumentProps {
  /** Raw markdown document body. */
  text: string;
  /** Class for the wrapping <div>. */
  className?: string;
}

export function MarkdownDocument({ text, className }: MarkdownDocumentProps) {
  return (
    <div className={className}>
      {parseDocument(text).map((b, i) => renderBlock(b, i))}
    </div>
  );
}
